import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type ToastTone = 'info' | 'success' | 'error' | 'warning'

export interface Toast {
  id: string
  title: string
  message?: string
  tone: ToastTone
}

interface ToastContextValue {
  toasts: Toast[]
  notify: (input: { title: string; message?: string; tone?: ToastTone }) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const notify = useCallback(
    (input: { title: string; message?: string; tone?: ToastTone }) => {
      const id = crypto.randomUUID()
      setToasts((list) => [
        ...list,
        { id, title: input.title, message: input.message, tone: input.tone ?? 'info' },
      ])
      window.setTimeout(() => dismiss(id), 4500)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toasts, notify, dismiss }), [toasts, notify, dismiss])

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
